import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle, Loader2, Plus, Search, MapPin, Phone, Building2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';

interface SearchResult {
  place_id: string;
  name: string;
  address: string;
  phone?: string;
  location: {
    lat: number;
    lng: number;
  };
  selected?: boolean;
}

interface LocationData {
  cities: any[];
  districts: any[];
  neighborhoods: any[];
}

export default function BulkTaxiImport() {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [locationData, setLocationData] = useState<LocationData>({
    cities: [],
    districts: [],
    neighborhoods: []
  });
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [selectedDistrict, setSelectedDistrict] = useState<string>('');
  const [searchResults, setSearchResults] = useState<{[key: string]: SearchResult[]}>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [progress, setProgress] = useState<{current: number, total: number}>({ current: 0, total: 0 });
  const [selectedPlaces, setSelectedPlaces] = useState<Set<string>>(new Set());
  const mapsLoaded = useGoogleMaps();

  useEffect(() => {
    loadCities();
  }, []);

  useEffect(() => {
    if (selectedCity) {
      loadDistricts(selectedCity);
      setSelectedDistrict('');
      setSearchResults({});
    }
  }, [selectedCity]);

  useEffect(() => {
    if (selectedDistrict) {
      loadNeighborhoods(selectedDistrict);
      setSearchResults({});
    }
  }, [selectedDistrict]);

  useEffect(() => {
    if (mapsLoaded && mapRef.current && !map) {
      const mapInstance = new google.maps.Map(mapRef.current, {
        center: { lat: 41.0082, lng: 28.9784 }, // İstanbul merkezi
        zoom: 12,
        styles: [
          {
            featureType: "all",
            elementType: "geometry",
            stylers: [{ color: "#242f3e" }]
          },
          {
            featureType: "all",
            elementType: "labels.text.stroke",
            stylers: [{ color: "#242f3e" }]
          },
          {
            featureType: "all",
            elementType: "labels.text.fill",
            stylers: [{ color: "#746855" }]
          }
        ]
      });
      setMap(mapInstance);
    }
  }, [mapsLoaded, mapRef.current]);

  const loadCities = async () => {
    try {
      const { data: cities, error } = await supabase
        .from('cities')
        .select('*')
        .order('name');
      
      if (error) throw error;
      setLocationData(prev => ({ ...prev, cities: cities || [] }));
    } catch (err) {
      console.error('Error loading cities:', err);
      setError('Şehirler yüklenirken bir hata oluştu.');
    }
  };

  const loadDistricts = async (cityId: string) => {
    try {
      const { data: districts, error } = await supabase
        .from('districts')
        .select('*')
        .eq('city_id', cityId)
        .order('name');
      
      if (error) throw error;
      setLocationData(prev => ({ ...prev, districts: districts || [] }));
    } catch (err) {
      console.error('Error loading districts:', err);
      setError('İlçeler yüklenirken bir hata oluştu.');
    }
  };

  const loadNeighborhoods = async (districtId: string) => {
    try {
      const { data: neighborhoods, error } = await supabase
        .from('neighborhoods')
        .select('*')
        .eq('district_id', districtId)
        .order('name');
      
      if (error) throw error;
      setLocationData(prev => ({ ...prev, neighborhoods: neighborhoods || [] }));
    } catch (err) {
      console.error('Error loading neighborhoods:', err);
      setError('Mahalleler yüklenirken bir hata oluştu.');
    }
  };

  const searchTaxiStands = async (neighborhood: any) => {
    if (!map) return [];

    const service = new google.maps.places.PlacesService(map);
    const searchQuery = `${neighborhood.name} taksi durağı`;

    try {
      const selectedCityData = locationData.cities.find(c => c.id === selectedCity);
      const selectedDistrictData = locationData.districts.find(d => d.id === selectedDistrict);

      if (!selectedCityData || !selectedDistrictData) {
        throw new Error('Şehir veya ilçe bilgisi bulunamadı');
      }

      const results = await new Promise<google.maps.places.PlaceResult[]>((resolve, reject) => {
        service.textSearch({
          query: searchQuery,
          type: 'establishment'
        }, (results, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && results) {
            resolve(results);
          } else {
            reject(new Error('Arama sonuçları alınamadı'));
          }
        });
      });

      const detailedResults = await Promise.all(
        results.slice(0, 7).map(async (result) => {
          if (!result.place_id) return null;

          try {
            const details = await new Promise<google.maps.places.PlaceResult>((resolve, reject) => {
              service.getDetails({
                placeId: result.place_id,
                fields: ['name', 'formatted_address', 'formatted_phone_number', 'geometry', 'address_components']
              }, (place, status) => {
                if (status === google.maps.places.PlacesServiceStatus.OK && place) {
                  resolve(place);
                } else {
                  reject(new Error('Detaylı bilgiler alınamadı'));
                }
              });
            });

            if (!details.formatted_phone_number) return null;

            const addressComponents = details.address_components || [];
            const cityComponent = addressComponents.find(component => 
              component.types.includes('administrative_area_level_1')
            );
            const districtComponent = addressComponents.find(component =>
              component.types.includes('administrative_area_level_2')
            );

            const cityMatches = cityComponent?.long_name.toLowerCase().includes(selectedCityData.name.toLowerCase());
            const districtMatches = districtComponent?.long_name.toLowerCase().includes(selectedDistrictData.name.toLowerCase());

            if (!cityMatches || !districtMatches) {
              console.log(`Adres eşleşmedi: ${details.formatted_address}`);
              console.log(`Beklenen: ${selectedCityData.name}, ${selectedDistrictData.name}`);
              console.log(`Bulunan: ${cityComponent?.long_name}, ${districtComponent?.long_name}`);
              return null;
            }

            return {
              place_id: result.place_id,
              name: details.name || '',
              address: details.formatted_address || '',
              phone: details.formatted_phone_number,
              location: {
                lat: details.geometry?.location?.lat() || 0,
                lng: details.geometry?.location?.lng() || 0
              }
            };
          } catch (error) {
            console.error('Place details error:', error);
            return null;
          }
        })
      );

      return detailedResults.filter((r): r is SearchResult => r !== null);
    } catch (error) {
      console.error('Search error:', error);
      return [];
    }
  };

  const handleBulkSearch = async () => {
    if (!selectedDistrict) {
      setError('Lütfen bir ilçe seçin');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setSearchResults({});

    const neighborhoods = locationData.neighborhoods;
    setProgress({ current: 0, total: neighborhoods.length });

    try {
      for (let i = 0; i < neighborhoods.length; i++) {
        const neighborhood = neighborhoods[i];
        const results = await searchTaxiStands(neighborhood);
        
        if (results.length > 0) {
          setSearchResults(prev => ({
            ...prev,
            [neighborhood.id]: results
          }));
        }

        setProgress({ current: i + 1, total: neighborhoods.length });
        
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      setSuccess('Arama tamamlandı. Kaydetmek istediğiniz taksi duraklarını seçin.');
    } catch (err) {
      console.error('Bulk search error:', err);
      setError('Toplu arama sırasında bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePlace = (placeId: string) => {
    setSelectedPlaces(prev => {
      const newSet = new Set(prev);
      if (newSet.has(placeId)) {
        newSet.delete(placeId);
      } else {
        newSet.add(placeId);
      }
      return newSet;
    });
  };

  const handleToggleNeighborhood = (neighborhoodId: string, results: SearchResult[]) => {
    setSelectedPlaces(prev => {
      const newSet = new Set(prev);
      const placeIds = results.map(r => r.place_id);
      
      const allSelected = placeIds.every(id => newSet.has(id));
      
      if (allSelected) {
        placeIds.forEach(id => newSet.delete(id));
      } else {
        placeIds.forEach(id => newSet.add(id));
      }
      
      return newSet;
    });
  };

  const handleSaveSelected = async () => {
    if (!selectedDistrict || selectedPlaces.size === 0) {
      setError('Lütfen en az bir taksi durağı seçin');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const taxiStands = Object.entries(searchResults)
        .flatMap(([neighborhoodId, results]) =>
          results
            .filter(result => selectedPlaces.has(result.place_id))
            .map(result => ({
              name: result.name,
              address: result.address,
              phone: result.phone?.replace(/[^0-9]/g, '') || '',
              latitude: result.location.lat,
              longitude: result.location.lng,
              neighborhood_id: neighborhoodId,
              is_verified: true,
              available_taxis: 0,
              total_taxis: 0,
              working_hours: '7/24'
            }))
        );

      if (taxiStands.length === 0) {
        setError('Kaydedilecek taksi durağı seçilmedi.');
        return;
      }

      const { error } = await supabase
        .from('taxi_stands')
        .insert(taxiStands);

      if (error) throw error;

      setSuccess(`${taxiStands.length} taksi durağı başarıyla kaydedildi.`);
      setSearchResults({});
      setSelectedPlaces(new Set());
      
    } catch (err) {
      console.error('Save error:', err);
      setError('Kayıt sırasında bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-white">Toplu Taksi Durağı Ekle</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="bg-gray-800 rounded-xl p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-xl shadow-lg shadow-yellow-400/20">
                <Building2 className="w-6 h-6 text-gray-900" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Konum Seçimi</h2>
                <p className="text-sm text-gray-400">Aramak istediğiniz bölgeyi seçin</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Şehir Seçin
                </label>
                <select
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                >
                  <option value="">Seçiniz</option>
                  {locationData.cities.map(city => (
                    <option key={city.id} value={city.id}>{city.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  İlçe Seçin
                </label>
                <select
                  value={selectedDistrict}
                  onChange={(e) => setSelectedDistrict(e.target.value)}
                  disabled={!selectedCity}
                  className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-400 disabled:opacity-50"
                >
                  <option value="">Seçiniz</option>
                  {locationData.districts.map(district => (
                    <option key={district.id} value={district.id}>{district.name}</option>
                  ))}
                </select>
              </div>

              <button
                onClick={handleBulkSearch}
                disabled={!selectedDistrict || loading}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>
                      Aranıyor... ({progress.current}/{progress.total})
                    </span>
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5" />
                    <span>Tüm Mahallelerde Ara</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <p className="text-red-400">{error}</p>
            </div>
          )}

          {success && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 text-green-400">
              {success}
            </div>
          )}

          {Object.keys(searchResults).length > 0 && (
            <div className="bg-gray-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    Bulunan Taksi Durakları
                  </h3>
                  <p className="text-sm text-gray-400 mt-1">
                    {selectedPlaces.size} durak seçildi
                  </p>
                </div>
                <button
                  onClick={handleSaveSelected}
                  disabled={loading || selectedPlaces.size === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  <span>Seçilenleri Kaydet ({selectedPlaces.size})</span>
                </button>
              </div>

              <div className="space-y-6">
                {Object.entries(searchResults).map(([neighborhoodId, results]) => {
                  const neighborhood = locationData.neighborhoods.find(n => n.id === neighborhoodId);
                  
                  if (!neighborhood || results.length === 0) return null;

                  const allSelected = results.every(r => selectedPlaces.has(r.place_id));
                  const someSelected = results.some(r => selectedPlaces.has(r.place_id));

                  return (
                    <div key={neighborhoodId} className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-white font-medium">{neighborhood.name}</h4>
                        <button
                          onClick={() => handleToggleNeighborhood(neighborhoodId, results)}
                          className="text-sm text-yellow-400 hover:text-yellow-300"
                        >
                          {allSelected ? 'Tümünü Kaldır' : 'Tümünü Seç'}
                        </button>
                      </div>
                      <div className="space-y-2">
                        {results.map((result) => (
                          <div
                            key={result.place_id}
                            className={`bg-gray-700/50 rounded-lg p-4 cursor-pointer transition-colors ${
                              selectedPlaces.has(result.place_id)
                                ? 'bg-yellow-400/10 border border-yellow-400/20'
                                : 'hover:bg-gray-700 border border-transparent'
                            }`}
                            onClick={() => handleTogglePlace(result.place_id)}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`p-2 rounded-lg ${
                                selectedPlaces.has(result.place_id)
                                  ? 'bg-yellow-400/10'
                                  : 'bg-gray-700'
                              }`}>
                                <MapPin className={`w-5 h-5 ${
                                  selectedPlaces.has(result.place_id)
                                    ? 'text-yellow-400'
                                    : 'text-gray-400'
                                }`} />
                              </div>
                              <div className="flex-1">
                                <h5 className="text-white font-medium">{result.name}</h5>
                                <p className="text-sm text-gray-400 mt-1">{result.address}</p>
                                <div className="flex items-center gap-2 mt-2">
                                  <Phone className="w-4 h-4 text-green-400" />
                                  <p className="text-sm text-green-400">{result.phone}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="lg:sticky lg:top-8">
          <div className="bg-gray-800 rounded-xl p-6">
            <div 
              ref={mapRef} 
              className="w-full h-[600px] rounded-lg overflow-hidden"
            />
          </div>
        </div>
      </div>
    </div>
  );
}